import React from "react";
import { COLORS } from "../../theme/colors";
import { RADIUS, MATERIALS } from "../../theme/materials";

// Batch B6 — in-app RBAC permission matrix for Admin → Users & Roles.
//
// Admin → Users & Roles lets an owner ASSIGN roles but never showed WHAT each
// role can do. This surfaces the same permission matrix as the shipped guide at
// frontend/public/help/rbac-roles.html so the in-app view matches the docs.
//
// The five built-in roles form a single CUMULATIVE hierarchy — a higher role can
// do everything the roles below it can, plus more. `ROLES` is ordered
// lowest-privilege → highest so a cell is ✓ from a capability's `minRole`
// rightward.
export const ROLES = [
  { id: "business_user", label: "Business User" },
  { id: "supervisor", label: "Supervisor" },
  { id: "developer", label: "Developer" },
  { id: "administrator", label: "Administrator" },
  { id: "owner", label: "Owner" },
];

// Rank of each role in the cumulative hierarchy (index in ROLES).
// owner(4) > administrator(3) > developer(2) > supervisor(1) > business_user(0).
const ROLE_RANK = ROLES.reduce((acc, r, i) => {
  acc[r.id] = i;
  return acc;
}, {});

// Capability → minimum role that unlocks it. Grounded in the real require_*
// gates on the API endpoints; mirrors the matrix rows + footnotes in
// frontend/public/help/rbac-roles.html (v1, 2026-08-18). The row for a
// read-vs-write split reflects the MANAGEMENT (write) bar, per the guide.
export const CAPABILITIES = [
  { capability: "Chat with agents", minRole: "business_user" },
  { capability: "View agents", minRole: "business_user" },
  { capability: "Analytics & dashboards", minRole: "business_user" },
  { capability: "Knowledge — search & stats", minRole: "business_user" },
  { capability: "Supervisor / approvals", minRole: "supervisor" },
  { capability: "Customer identity & memory", minRole: "supervisor" },
  { capability: "Agents — build & edit", minRole: "developer" },
  { capability: "Knowledge — ingestion", minRole: "developer" },
  { capability: "MCP integrations", minRole: "developer" },
  { capability: "Channels — bind to agent", minRole: "developer" },
  { capability: "Agents — delete", minRole: "administrator" },
  { capability: "Channels & telephony config", minRole: "administrator" },
  { capability: "Admin settings & credentials", minRole: "administrator" },
  { capability: "OpenAPI integrations", minRole: "administrator" },
  { capability: "Billing — change plan/quotas", minRole: "administrator" },
  { capability: "Compliance & audit", minRole: "administrator" },
  { capability: "Conversation Inspector", minRole: "administrator" },
  { capability: "Users & roles", minRole: "owner" },
];

// Pure predicate: does `role` have a capability whose minimum role is `minRole`?
// True when `role` sits at or above `minRole` in the cumulative hierarchy.
// Unknown role ids resolve to rank -1 (never satisfies) so a typo fails closed.
export function hasCapability(minRole, role) {
  const need = ROLE_RANK[minRole];
  const have = ROLE_RANK[role];
  if (need === undefined || have === undefined) return false;
  return have >= need;
}

export default function RbacPermissionMatrix() {
  const cellBorder = `1px solid ${COLORS.border}`;
  return (
    <div
      className="table-scroll"
      style={{
        background: COLORS.surface,
        border: `1px solid ${COLORS.border}`,
        borderRadius: RADIUS.inner,
        boxShadow: MATERIALS.spatialShadow(),
        padding: "20px 24px",
        overflowX: "auto",
        marginTop: 16,
      }}
    >
      <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.text, letterSpacing: "-0.01em" }}>
        Permission matrix
      </div>
      <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 4, marginBottom: 16 }}>
        Roles are cumulative — a higher role can do everything the roles to its left can. A
        capability is available from its minimum role rightward.
      </div>
      <table
        role="table"
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: 12,
          minWidth: 640,
        }}
      >
        <thead>
          <tr>
            <th
              style={{
                textAlign: "left",
                padding: "0 12px 12px 0",
                fontSize: 10,
                fontWeight: 700,
                color: COLORS.textDim,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                borderBottom: cellBorder,
              }}
            >
              Capability area
            </th>
            {ROLES.map((r) => (
              <th
                key={r.id}
                style={{
                  textAlign: "center",
                  padding: "0 8px 12px",
                  fontSize: 10,
                  fontWeight: 700,
                  color: COLORS.textDim,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  borderBottom: cellBorder,
                }}
              >
                {r.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {CAPABILITIES.map((row) => (
            <tr key={row.capability}>
              <td
                style={{
                  textAlign: "left",
                  padding: "12px 12px 12px 0",
                  color: COLORS.text,
                  fontWeight: 500,
                  borderBottom: cellBorder,
                }}
              >
                {row.capability}
              </td>
              {ROLES.map((r) => {
                const allowed = hasCapability(row.minRole, r.id);
                return (
                  <td
                    key={r.id}
                    aria-label={`${row.capability} — ${r.label}: ${allowed ? "allowed" : "blocked"}`}
                    style={{
                      textAlign: "center",
                      padding: "12px 8px",
                      fontWeight: 700,
                      color: allowed ? COLORS.green : COLORS.textDim,
                      borderBottom: cellBorder,
                    }}
                  >
                    {allowed ? "✓" : "✗"}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
